import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiMutation, useApiQuery } from '../../api/hooks';
import { ApiError } from '../../api/problem';
import type {
  ChangeClinicStatusRequest,
  ClinicResponse,
  ClinicStatus,
  CreateClinicRequest,
  FeatureFlagResponse,
  InviteStaffResponse,
  ProvisionAdministratorRequest,
  ResetPasswordRequest,
} from '../../api/types';
import { MINIMUM_PASSWORD_LENGTH } from '../../auth/passwords';
import { AsyncSection } from '../../components/AsyncSection';
import { PageHeader } from '../../components/PageHeader';
import { ErrorState } from '../../components/states/StateViews';
import { useLocalisedName } from '../../i18n/localisedName';

/** The rungs a clinic can be moved to from where it currently stands (DEC-047). */
const NEXT_RUNGS: Record<ClinicStatus, readonly ClinicStatus[]> = {
  PROVISIONING: ['ACTIVE'],
  ACTIVE: ['OVERDUE'],
  OVERDUE: ['READ_ONLY', 'ACTIVE'],
  READ_ONLY: ['SUSPENDED', 'ACTIVE'],
  SUSPENDED: ['ACTIVE'],
};

/**
 * The platform console's tenant list (FR-107, DEC-047).
 *
 * The status control offers only the moves the ladder permits, so an operator cannot suspend a
 * clinic that has not first been made read-only — the API refuses it anyway, and an interface that
 * offers an action it knows will fail is an interface that teaches people to ignore it.
 *
 * Everything shown here is about a clinic rather than inside one: name, price, branch count, unpaid
 * invoices. There is no route from this screen to a patient (SEC-003).
 */
export function PlatformClinicsPage() {
  const localised = useLocalisedName();
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [adminFor, setAdminFor] = useState<{ readonly clinicId: string; readonly task: AdministratorTask } | null>(
    null,
  );
  const openAdministratorTask = (clinicId: string | undefined, task: AdministratorTask) =>
    setAdminFor((current) =>
      current?.clinicId === clinicId && current?.task === task ? null : clinicId ? { clinicId, task } : null,
    );

  const clinics = useApiQuery<ClinicResponse[]>(queryKeys.platformClinics(), '/platform/clinics');

  const createClinic = useApiMutation<ClinicResponse, CreateClinicRequest>(
    (body) => ({ path: '/platform/clinics', options: { method: 'POST', body } }),
    { invalidates: [queryKeys.platformClinics()], onSuccess: () => setCreating(false) },
  );

  const changeStatus = useApiMutation<
    ClinicResponse,
    { readonly clinicId: string } & ChangeClinicStatusRequest
  >(
    ({ clinicId, ...body }) => ({
      path: `/platform/clinics/${clinicId}/status`,
      options: { method: 'POST', body },
    }),
    { invalidates: [queryKeys.platformClinics()] },
  );

  return (
    <>
      <PageHeader
        title={t('nav.items.tenants')}
        description={t('platform.clinicsDescription')}
        actions={
          <button type="button" className="button button--primary" onClick={() => setCreating((open) => !open)}>
            {creating ? t('common.cancel') : t('platform.addClinic')}
          </button>
        }
      />

      {creating ? (
        <form
          className="panel"
          style={{ marginBlockEnd: 'var(--space-5)' }}
          onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            const price = String(form.get('price') ?? '').trim();
            // No key: the server gives the next clinic code, cl01, cl02… (CR-24).
            createClinic.mutate({
              legalName: String(form.get('legalName') ?? '').trim(),
              displayNameAr: String(form.get('displayNameAr') ?? '').trim() || undefined,
              displayNameEn: String(form.get('displayNameEn') ?? '').trim() || undefined,
              subscriptionUnitPrice: price ? { amount: price, currency: 'EGP' } : undefined,
            });
          }}
        >
          <div className="form-grid">
            <label className="field">
              <span className="field__label">{t('platform.legalName')}</span>
              <input name="legalName" className="field__input" required />
            </label>
            <label className="field">
              <span className="field__label">{t('common.nameAr')}</span>
              <input name="displayNameAr" className="field__input" />
            </label>
            <label className="field">
              <span className="field__label">{t('common.nameEn')}</span>
              <input name="displayNameEn" className="field__input ltr" />
            </label>
            <label className="field">
              <span className="field__label">{t('platform.unitPrice')}</span>
              <input name="price" className="field__input numeric" defaultValue="3000.00" inputMode="decimal" />
              <span className="field__hint">{t('platform.unitPriceHint')}</span>
            </label>
          </div>

          {createClinic.error ? <ErrorState error={createClinic.error} /> : null}

          <div style={{ marginBlockStart: 'var(--space-4)' }}>
            <button type="submit" className="button button--primary" disabled={createClinic.isPending}>
              {createClinic.isPending ? t('branches.saving') : t('platform.createClinic')}
            </button>
            <p className="field__hint">{t('platform.createClinicHint')}</p>
          </div>
        </form>
      ) : null}

      <AsyncSection
        query={clinics}
        isEmpty={(rows) => rows.length === 0}
        emptyTitle={t('platform.emptyTitle')}
        emptyDescription={t('platform.emptyDescription')}
      >
        {(rows) => (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('platform.clinic')}</th>
                  <th scope="col">{t('platform.code')}</th>
                  <th scope="col">{t('common.status')}</th>
                  <th scope="col">{t('platform.administrator')}</th>
                  <th scope="col">{t('platform.branches')}</th>
                  <th scope="col">{t('platform.unitPrice')}</th>
                  <th scope="col">{t('platform.overdue')}</th>
                  <th scope="col">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((clinic) => (
                  <tr key={clinic.id}>
                    <th scope="row">{localised.displayName(clinic, clinic.legalName)}</th>
                    {/* The key is stored in lower case; the code is read in capitals, CL01 (CR-24). */}
                    <td className="ltr">{clinic.slug?.toUpperCase()}</td>
                    <td>
                      <span className={statusBadge(clinic.status)}>
                        {t(`platform.status.${clinic.status ?? 'PROVISIONING'}`)}
                      </span>
                    </td>
                    <td>
                      {clinic.administrator ? (
                        <>
                          {clinic.administrator.displayName}
                          <br />
                          <span className="ltr field__hint">{clinic.administrator.email}</span>
                        </>
                      ) : (
                        <span className="badge badge--muted">{t('platform.noAdministrator')}</span>
                      )}
                    </td>
                    <td className="numeric">{clinic.billableBranches}</td>
                    <td className="numeric">{clinic.subscriptionUnitPrice?.amount ?? '—'}</td>
                    <td className="numeric">
                      {clinic.overdueInvoices ? (
                        <span className="badge badge--danger">{clinic.overdueInvoices}</span>
                      ) : (
                        '0'
                      )}
                    </td>
                    <td>
                      <div className="table__actions">
                        {(NEXT_RUNGS[clinic.status ?? 'PROVISIONING'] ?? []).map((target) => (
                          <button
                            key={target}
                            type="button"
                            className="button button--ghost"
                            disabled={changeStatus.isPending}
                            onClick={() => {
                              const reason = globalThis.prompt(t('platform.statusReasonPrompt')) ?? '';
                              if (reason.trim()) {
                                changeStatus.mutate({
                                  clinicId: clinic.id ?? '',
                                  status: target,
                                  reason: reason.trim(),
                                });
                              }
                            }}
                          >
                            {/* Leaving provisioning is a hand-over, not a restoration: the clinic
                                was never live, so "Restore" would describe something that did not
                                happen. */}
                            {clinic.status === 'PROVISIONING'
                              ? t('platform.goLive')
                              : t(`platform.moveTo.${target}`)}
                          </button>
                        ))}
                        {/* A clinic has exactly one administrator (CR-15): the first one while it has
                            none, afterwards only a replacement or a new password for the one it has. */}
                        {clinic.administrator ? (
                          <>
                            <button
                              type="button"
                              className="button button--ghost"
                              onClick={() => openAdministratorTask(clinic.id, 'replace')}
                            >
                              {t('platform.replaceAdmin')}
                            </button>
                            <button
                              type="button"
                              className="button button--ghost"
                              onClick={() => openAdministratorTask(clinic.id, 'reset')}
                            >
                              {t('platform.resetAdmin')}
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="button button--ghost"
                            onClick={() => openAdministratorTask(clinic.id, 'first')}
                          >
                            {t('platform.firstAdmin')}
                          </button>
                        )}
                        <button
                          type="button"
                          className="button button--ghost"
                          onClick={() =>
                            setExpanded((current) => (current === clinic.id ? null : (clinic.id ?? null)))
                          }
                        >
                          {expanded === clinic.id ? t('platform.hideFlags') : t('nav.items.featureFlags')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AsyncSection>

      {changeStatus.error ? <ErrorState error={changeStatus.error} /> : null}

      {adminFor && clinics.data ? (
        adminFor.task === 'reset' ? (
          <ResetAdministratorSection
            key={`${adminFor.clinicId}-reset`}
            clinic={clinics.data.find((clinic) => clinic.id === adminFor.clinicId) ?? { id: adminFor.clinicId }}
          />
        ) : (
          <AdministratorSection
            key={`${adminFor.clinicId}-${adminFor.task}`}
            task={adminFor.task}
            clinic={clinics.data.find((clinic) => clinic.id === adminFor.clinicId) ?? { id: adminFor.clinicId }}
          />
        )
      ) : null}

      {expanded ? <FeatureFlagsSection clinicId={expanded} /> : null}
    </>
  );
}

type AdministratorTask = 'first' | 'replace' | 'reset';

/**
 * A clinic's administrator, created or replaced (FR-107, ASM-007, CR-15).
 *
 * A clinic the platform creates has nobody who can sign in to it, so the first administrator is the
 * step that makes onboarding possible at all. A clinic has exactly one administrator and cannot
 * change it itself, so replacing them when they leave is the platform's too. Everyone else is the
 * clinic's own business (SEC-003).
 *
 * The operator chooses the administrator's first password and passes it on with the email
 * address; the administrator signs in on their own machine and can change it from their account.
 * The field is plain text for the same reason as on the staff screen: it is a password to hand
 * over, not the operator's own.
 */
function AdministratorSection({
  clinic,
  task,
}: {
  readonly clinic: ClinicResponse;
  readonly task: 'first' | 'replace';
}) {
  const localised = useLocalisedName();
  const { t } = useTranslation();
  const replacing = task === 'replace';
  const clinicName = localised.displayName(clinic, clinic.legalName ?? clinic.slug);

  const provision = useApiMutation<InviteStaffResponse, ProvisionAdministratorRequest>(
    (body) => ({
      path: `/platform/clinics/${clinic.id}/administrator`,
      options: { method: replacing ? 'PUT' : 'POST', body },
    }),
    { invalidates: [queryKeys.platformClinics()] },
  );
  const issued = provision.data;

  return (
    <section className="section" aria-labelledby="admin-heading">
      <h2 className="section__title" id="admin-heading">
        {replacing
          ? t('platform.replaceAdminTitle', { clinic: clinicName })
          : t('platform.firstAdminTitle', { clinic: clinicName })}
      </h2>

      {issued ? (
        <AdministratorHandover issued={issued} />
      ) : (
        <form
          className="panel"
          onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            provision.mutate({
              displayName: String(form.get('displayName') ?? '').trim(),
              email: String(form.get('email') ?? '').trim(),
              preferredLocale: String(form.get('preferredLocale') ?? 'ar'),
              password: String(form.get('password') ?? ''),
            });
          }}
        >
          <p style={{ marginBlockEnd: 'var(--space-4)' }}>
            {replacing
              ? t('platform.replaceAdminDescription', {
                  name: clinic.administrator?.displayName ?? clinic.administrator?.email ?? '',
                })
              : t('platform.firstAdminDescription')}
          </p>
          <div className="form-grid">
            <label className="field">
              <span className="field__label">{t('staff.name')}</span>
              <input name="displayName" className="field__input" required maxLength={160} />
            </label>
            <label className="field">
              <span className="field__label">{t('staff.email')}</span>
              <input name="email" type="email" className="field__input ltr" required maxLength={320} />
              <span className="field__hint">
                {replacing ? t('platform.replaceAdminEmailHint') : t('platform.firstAdminEmailHint')}
              </span>
            </label>
            <label className="field">
              <span className="field__label">{t('staff.password')}</span>
              <input
                name="password"
                className="field__input ltr"
                required
                minLength={MINIMUM_PASSWORD_LENGTH}
                maxLength={200}
                autoComplete="off"
                spellCheck={false}
              />
              <span className="field__hint">{t('staff.passwordHint', { n: MINIMUM_PASSWORD_LENGTH })}</span>
            </label>
            <label className="field">
              <span className="field__label">{t('common.language')}</span>
              <select
                name="preferredLocale"
                className="field__input"
                defaultValue={clinic.defaultLocale === 'en' ? 'en' : 'ar'}
              >
                <option value="ar">{t('common.arabic')}</option>
                <option value="en">{t('common.english')}</option>
              </select>
            </label>
          </div>

          {provision.error ? <ErrorState error={provision.error} /> : null}

          <div style={{ marginBlockStart: 'var(--space-4)' }}>
            <button type="submit" className="button button--primary" disabled={provision.isPending}>
              {provision.isPending
                ? t('branches.saving')
                : replacing
                  ? t('platform.replaceAdmin')
                  : t('platform.createAdmin')}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

/**
 * A new password for a clinic's administrator (CR-15): the clinic cannot give one, because nobody
 * there outranks them. An administrator who also uses their account at another clinic owns that
 * password (DEC-012), and the refusal says so in the reader's language.
 */
function ResetAdministratorSection({ clinic }: { readonly clinic: ClinicResponse }) {
  const localised = useLocalisedName();
  const { t } = useTranslation();
  const name = clinic.administrator?.displayName ?? clinic.administrator?.email ?? '';

  const reset = useApiMutation<void, ResetPasswordRequest>((body) => ({
    path: `/platform/clinics/${clinic.id}/administrator/password`,
    options: { method: 'POST', body },
  }));
  const shared = reset.error instanceof ApiError && reset.error.problem.rule === 'DEC-012';

  return (
    <section className="section" aria-labelledby="admin-reset-heading">
      <h2 className="section__title" id="admin-reset-heading">
        {t('platform.resetAdminTitle', { clinic: localised.displayName(clinic, clinic.legalName ?? clinic.slug) })}
      </h2>

      {reset.isSuccess ? (
        <div className="callout" role="status">
          <p>{t('staff.resetDone', { name })}</p>
        </div>
      ) : (
        <form
          className="panel"
          onSubmit={(event) => {
            event.preventDefault();
            reset.mutate({ password: String(new FormData(event.currentTarget).get('password') ?? '') });
          }}
        >
          <label className="field">
            <span className="field__label">{t('staff.resetTitle', { name })}</span>
            <input
              name="password"
              className="field__input ltr"
              required
              minLength={MINIMUM_PASSWORD_LENGTH}
              maxLength={200}
              autoComplete="off"
              spellCheck={false}
            />
            <span className="field__hint">{t('staff.resetHint', { n: MINIMUM_PASSWORD_LENGTH })}</span>
          </label>

          {shared ? (
            <p className="field__error" role="alert">
              {t('platform.resetAdminShared')}
            </p>
          ) : reset.error ? (
            <ErrorState error={reset.error} />
          ) : null}

          <div style={{ marginBlockStart: 'var(--space-4)' }}>
            <button type="submit" className="button button--primary" disabled={reset.isPending}>
              {reset.isPending ? t('branches.saving') : t('staff.resetSubmit')}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

/** Word that the administrator can sign in now, or that they already had an account of their own. */
function AdministratorHandover({ issued }: { readonly issued: InviteStaffResponse }) {
  const { t } = useTranslation();

  if (!issued.passwordSet) {
    return (
      <div className="callout" role="status">
        <p className="state__title">{t('staff.existingAccountTitle')}</p>
        <p>{t('staff.existingAccountDescription')}</p>
      </div>
    );
  }

  return (
    <div className="callout" role="status">
      <p className="state__title">{t('platform.adminCreatedTitle', { email: issued.member?.email ?? '' })}</p>
      <p>{t('platform.adminCreatedDescription')}</p>
    </div>
  );
}

/**
 * Per-clinic feature flags (FR-108).
 *
 * Every declared flag is listed, whether or not the clinic has a setting of its own, with the ones
 * still on their default marked as such — otherwise "off" and "never configured" look identical,
 * which is the exact ambiguity a flag exists to remove.
 */
export function FeatureFlagsSection({ clinicId }: { readonly clinicId: string }) {
  const { t } = useTranslation();

  const flags = useApiQuery<FeatureFlagResponse[]>(
    queryKeys.platformFeatureFlags(clinicId),
    `/platform/clinics/${clinicId}/feature-flags`,
  );

  const setFlag = useApiMutation<
    FeatureFlagResponse,
    { readonly key: string; readonly enabled: boolean }
  >(
    ({ key, enabled }) => ({
      path: `/platform/clinics/${clinicId}/feature-flags/${key}`,
      options: { method: 'PUT', body: { enabled } },
    }),
    { invalidates: [queryKeys.platformFeatureFlags(clinicId)] },
  );

  return (
    <section className="section" aria-labelledby="flags-heading">
      <h2 className="section__title" id="flags-heading">
        {t('nav.items.featureFlags')}
      </h2>

      <AsyncSection query={flags} isEmpty={(rows) => rows.length === 0}>
        {(rows) => (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('platform.flag')}</th>
                  <th scope="col">{t('platform.enabled')}</th>
                  <th scope="col">{t('platform.source')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((flag) => (
                  <tr key={flag.key}>
                    <th scope="row">{t(`flags.${flag.key ?? 'PATIENT_APP'}`)}</th>
                    <td>
                      <input
                        type="checkbox"
                        checked={Boolean(flag.enabled)}
                        disabled={setFlag.isPending}
                        aria-label={t('platform.enabled')}
                        onChange={(event) =>
                          setFlag.mutate({ key: String(flag.key), enabled: event.target.checked })
                        }
                      />
                    </td>
                    <td>
                      <span className={flag.overridden ? 'badge' : 'badge badge--muted'}>
                        {flag.overridden ? t('platform.configured') : t('platform.default')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AsyncSection>

      {setFlag.error ? <ErrorState error={setFlag.error} /> : null}
    </section>
  );
}

function statusBadge(status: ClinicStatus | undefined): string {
  switch (status) {
    case 'ACTIVE':
      return 'badge';
    case 'OVERDUE':
    case 'READ_ONLY':
      return 'badge badge--warning';
    case 'SUSPENDED':
      return 'badge badge--danger';
    default:
      return 'badge badge--muted';
  }
}
